import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Send, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({
  type,
  page,
  accent = "var(--primary)",
  submitLabel = "Send",
  selects = [],
  showMessage = true,
  downloadUrl = null,
  successTitle = "Thank you — we've got it.",
  successBody = "Casey's team will reach out shortly.",
  testid = "lead-form",
}) => {
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [extra, setExtra] = useState(() => Object.fromEntries(selects.map((s) => [s.name, s.options[0]])));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@")) {
      setErr("Please add your name and a valid email.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { type, page, ...form, meta: extra });
      setDone(true);
    } catch (e2) {
      setErr("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full shape-r bg-black/25 border border-white/15 focus:outline-none focus:border-primary-c p-3.5 text-fg placeholder:text-fg/30 transition-colors";

  return (
    <div className="glass shape-r p-8 md:p-10" data-testid={testid}>
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid={`${testid}-success`}>
            <div className="grid place-items-center h-12 w-12 rounded-full mb-4" style={{ backgroundColor: `${accent}22`, color: accent }}>
              <Check size={22} />
            </div>
            <p className="font-serif text-2xl md:text-3xl leading-snug">{successTitle}</p>
            <p className="mt-3 text-fg/60 leading-relaxed">{successBody}</p>
            {downloadUrl && (
              <a
                href={downloadUrl}
                download
                data-testid={`${testid}-download`}
                className="inline-flex items-center gap-2 mt-6 shape-r font-medium px-6 py-3 transition-opacity hover:opacity-90"
                style={{ backgroundColor: accent, color: "#141b1d" }}
              >
                <Download size={16} /> Download your guide
              </a>
            )}
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={submit} initial={{ opacity: 1 }} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid={`${testid}-name`} className={field} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid={`${testid}-email`} className={field} placeholder="Email address" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <input data-testid={`${testid}-org`} className={field} placeholder="Organization / facility (optional)" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} />
            {selects.map((s) => (
              <div key={s.name}>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mb-1.5 block">{s.label}</label>
                <select
                  data-testid={`${testid}-${s.name}`}
                  className={field}
                  value={extra[s.name]}
                  onChange={(e) => setExtra({ ...extra, [s.name]: e.target.value })}
                >
                  {s.options.map((o) => <option key={o} value={o} className="bg-bg-elev">{o}</option>)}
                </select>
              </div>
            ))}
            {showMessage && (
              <textarea data-testid={`${testid}-message`} rows={3} className={`${field} resize-none`} placeholder="Tell us a little about your group…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            )}
            {err && <p className="text-sm" style={{ color: "#c46a58" }} data-testid={`${testid}-error`}>{err}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid={`${testid}-submit`}
              className="inline-flex items-center gap-2 shape-r font-medium px-7 py-3.5 transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: accent, color: "#141b1d" }}
            >
              <Send size={16} /> {busy ? "Sending…" : submitLabel}
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
